import { HttpClient } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { Observable, Subscription, mapTo } from 'rxjs';

export type DebugLoginTarget = 'portal' | 'demo-portal' | 'backoffice' | 'demo-backoffice';

/** A currently admitted actual identity returned by the debug BFF. */
export interface Persona {
  id: string;
  label: string;
  username: string;
  roles: string[];
  /**
   * Which group the picker files the account under, from the test-account catalog
   * (/bff/debug/users). Optional: an older Edge, or a user the catalog does not know, omits it.
   */
  audience?: 'customer' | 'staff' | null;
}

export interface DebugLoginHandoffResponse {
  target: DebugLoginTarget | 'backoffice-public' | 'demo-backoffice-public';
  redirectUrl: string;
  expiresAt: string;
}

/** Authoritative identity returned after the BFF installs the debug cookie session. */
export interface DebugLoginResponse {
  username: string;
  subject: string;
}

/**
 * Holds the selectable debug users and the one activated for the session. Clicking a user during
 * login establishes a real BFF cookie session via the dev-only `/bff/debug/login` endpoint.
 * Every selectable user comes from the debug BFF. Offline fixtures belong in mocked tests.
 */
@Injectable({ providedIn: 'root' })
export class PersonaService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly destroy = inject(DestroyRef);
  private request: Subscription | null = null;

  readonly personas = signal<Persona[]>([]);
  readonly loadState = signal<'idle' | 'loading' | 'ok' | 'error'>('idle');
  constructor() { this.destroy.onDestroy(() => this.request?.unsubscribe()); }

  /** The selected actual user, or null before login. */
  readonly active = signal<Persona | null>(null);
  readonly locked = computed(() => this.active() !== null);

  /** Refresh from the server only. A failed or empty read never revives a cached login choice. */
  loadPersonas(): void {
    this.request?.unsubscribe();
    this.loadState.set('loading');
    this.personas.set([]);
    this.request = this.http.get<Persona[]>('/bff/debug/users').subscribe({
      next: list => {
        if (!Array.isArray(list) || list.some(row => !row || typeof row.username !== 'string'
          || typeof row.id !== 'string' || typeof row.label !== 'string' || !Array.isArray(row.roles))) {
          this.personas.set([]); this.loadState.set('error'); return;
        }
        this.personas.set(list); this.loadState.set('ok');
      },
      error: () => { this.personas.set([]); this.loadState.set('error'); },
    });
  }

  /** Lock a debug actual user in for the session. */
  select(persona: Persona): void {
    this.active.set(persona);
  }

  /** Establish the real BFF cookie session for the selected seeded user. */
  login(persona: Persona): Observable<void> {
    return this.loginVerified(persona).pipe(mapTo(void 0));
  }

  /** Establish the session and retain the server-confirmed actual identity for strict callers. */
  loginVerified(persona: Persona): Observable<DebugLoginResponse> {
    return this.http.post<DebugLoginResponse>('/bff/debug/login', { username: persona.username });
  }

  /** Create a one-time, host-local login handoff for another BFF-backed surface. */
  createHandoff(persona: Persona, target: DebugLoginTarget): Observable<DebugLoginHandoffResponse> {
    const publicSource = ['debug.toybank.chocolatepi.net', 'debug.demo.chocolatepi.net',
      'debug.sim.chocolatepi.net', 'debug.backoffice.chocolatepi.net', 'debug.demobackoffice.chocolatepi.net']
      .includes((this.document.location?.hostname ?? '').toLowerCase());
    const serverTarget = publicSource && (target === 'backoffice' || target === 'demo-backoffice')
      ? `${target}-public` : target;
    return this.http.post<DebugLoginHandoffResponse>('/bff/debug/handoffs', {
      username: persona.username,
      target: serverTarget,
    });
  }

  reset(): void {
    this.active.set(null);
  }
}
